import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, LogOut, Mail, Building2, Download, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'tmg_admin_token';

const fmtDate = (s) => {
  try {
    const d = new Date(s);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s || '—';
  }
};

const AdminLeads = () => {
  const [token, setToken] = useState(
    typeof window !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) || '' : '',
  );
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('contact');

  const load = async (tkn) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/leads`, {
        headers: { 'X-Admin-Token': tkn },
      });
      setData(res.data);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, tkn);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unauthorized');
      setData(null);
      setAuthed(false);
      sessionStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken('');
    setAuthed(false);
    setData(null);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-ink-900 text-white grid place-items-center px-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(token);
          }}
          className="bg-ink-800 border border-white/10 p-8 max-w-md w-full"
          data-testid="admin-login-form"
        >
          <div className="overline text-amber mb-3">Admin · The Mobility Group</div>
          <h1 className="font-display text-2xl md:text-3xl mb-2">Sign in</h1>
          <p className="text-zinc-400 text-sm mb-6">
            Enter the admin token to view contact and atlas leads.
          </p>
          <label className="block mb-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-2">
              Admin token
            </span>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your token"
              data-testid="admin-token-input"
              className="w-full bg-ink-900 text-white placeholder:text-zinc-600 px-4 py-3 border border-white/10 outline-none focus:border-amber font-sans text-sm"
              autoFocus
              required
            />
          </label>
          {error && (
            <div className="mb-4 text-sm text-red-400 font-mono">{error}</div>
          )}
          <button
            type="submit"
            disabled={loading || !token}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  const contact = data?.contact_leads || [];
  const atlas = data?.atlas_leads || [];

  return (
    <div className="min-h-screen bg-ink-900 text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-10">
        <div className="px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="grid h-8 w-8 place-items-center border border-amber bg-amber/10 font-mono text-amber text-xs font-semibold">
              TMG
            </span>
            <span className="font-display text-base">Admin · Leads</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => load(token)}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-amber transition-colors"
              data-testid="admin-refresh"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Refresh
            </button>
            <button
              onClick={signOut}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-amber transition-colors"
              data-testid="admin-signout"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-10 py-10">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/5 border border-white/10 mb-8">
          <StatTile
            icon={<MessageSquare className="h-4 w-4 text-amber" />}
            label="Contact form leads"
            value={data?.counts?.contact ?? 0}
          />
          <StatTile
            icon={<Download className="h-4 w-4 text-cyan" />}
            label="Atlas downloads"
            value={data?.counts?.atlas ?? 0}
          />
          <StatTile
            icon={<Mail className="h-4 w-4 text-white" />}
            label="Total"
            value={(data?.counts?.contact ?? 0) + (data?.counts?.atlas ?? 0)}
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-white/10">
          {[
            ['contact', `Contact (${contact.length})`],
            ['atlas', `Atlas downloads (${atlas.length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] border-b-2 transition-colors ${
                tab === key
                  ? 'border-amber text-amber'
                  : 'border-transparent text-zinc-500 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Table */}
        {tab === 'contact' ? (
          <LeadTable
            rows={contact}
            cols={[
              { key: 'name', label: 'Name' },
              { key: 'email', label: 'Email' },
              { key: 'company', label: 'Company' },
              { key: 'role', label: 'Role' },
              { key: 'interest', label: 'Interest' },
              { key: 'message', label: 'Message', wide: true },
              { key: 'created_at', label: 'When', format: fmtDate },
            ]}
            empty="No contact submissions yet."
          />
        ) : (
          <LeadTable
            rows={atlas}
            cols={[
              { key: 'name', label: 'Name' },
              { key: 'email', label: 'Email' },
              { key: 'company', label: 'Company' },
              { key: 'role', label: 'Role' },
              { key: 'created_at', label: 'When', format: fmtDate },
            ]}
            empty="No atlas downloads yet."
          />
        )}
      </div>
    </div>
  );
};

const StatTile = ({ icon, label, value }) => (
  <div className="bg-ink-800 p-5">
    <div className="flex items-center justify-between mb-3">
      {icon}
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
        {label}
      </span>
    </div>
    <div className="font-display text-3xl text-white tabular-nums">{value}</div>
  </div>
);

const LeadTable = ({ rows, cols, empty }) => {
  if (!rows.length) {
    return (
      <div className="bg-ink-800 border border-white/10 p-10 text-center text-zinc-400 font-mono text-xs uppercase tracking-[0.18em]">
        {empty}
      </div>
    );
  }
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-leads-table">
        <thead className="bg-ink-800 border-b border-white/10">
          <tr>
            {cols.map((c) => (
              <th
                key={c.key}
                className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-white/5 hover:bg-ink-800/60">
              {cols.map((c) => {
                const raw = r[c.key];
                const v = c.format ? c.format(raw) : raw;
                return (
                  <td
                    key={c.key}
                    className={`px-4 py-3 align-top ${
                      c.wide ? 'max-w-md text-zinc-300' : 'whitespace-nowrap'
                    } ${c.key === 'email' ? 'text-amber font-mono text-xs' : 'text-zinc-200'}`}
                  >
                    {v || <span className="text-zinc-600">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminLeads;
